import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useIntl } from 'react-intl';
import { useSearchParams } from 'react-router-dom';
import {
  GetDecagonAuthSignatureResponse,
  GetPartnersDecagonJwtResponse,
} from '@classpass/protobuf-definitions/lib/web/grpc/auth/auth';

import URLS from 'constants/urls';
import braze, { BRAZE_CUSTOM_EVENTS } from 'helpers/braze';
import DatadogRum, { DATADOG_RUM_ACTIONS } from 'helpers/datadog-rum';
import { transformKeysToCamelCase } from 'helpers/core';
import { reportError } from 'helpers/error-logging';
import { httpClient } from 'helpers/http';
import { venuePropTypes } from 'constants/propTypes';

import { messages } from './Decagon.messages';

const messageChunkFormattingDict = {
  'privacy-policy-link': chunks =>
    `<a href="https://classpass.com/privacy" target="_blank" class="text-blue-200 font-bold">${chunks}</a>`,
};

function formatAllMessages(intl) {
  const allMessages = Object.entries(messages);

  const formattedMessagesObject = allMessages.reduce((acc, [key, messageDescriptor]) => {
    const formattedMessage = intl.formatMessage(messageDescriptor, messageChunkFormattingDict);

    acc[key] = formattedMessage;

    return acc;
  }, {});

  return formattedMessagesObject;
}

function triggerEngagedWithChatBotBraze() {
  braze.trackEvent(BRAZE_CUSTOM_EVENTS.ENGAGED_WITH_CHAT_BOT);
}

function setDecagonMessages(intl, decagonObject) {
  const decagonMessages = formatAllMessages(intl);

  decagonObject?.setLocalizationData?.(decagonMessages);
}

const ANONYMOUS_AUTH_EXPECTED_DENIAL_STATUSES = [401, 403];

const PARTNERS_JWT_EXPECTED_DENIAL_STATUSES = [401, 403];
const PARTNERS_JWT_REFRESH_LEEWAY_MS = 5 * 60 * 1000;
const PARTNERS_JWT_FALLBACK_LIFETIME_MS = 4 * 60 * 60 * 1000;
const PARTNERS_JWT_DENIAL_RETRY_BASE_MS = 60 * 1000;
const PARTNERS_JWT_DENIAL_RETRY_MAX_MS = 15 * 60 * 1000;

// An explicit `token: null` reaches our GraphQL API and is rejected as "Null Partners
// Decagon JWT", indistinguishable from a real auth failure; an absent key is not (IOPS-5713).
function withPartnersToken(metadata, token) {
  return token ? { ...metadata, token } : metadata;
}

function getJwtExpiryMs(token) {
  try {
    const payload = token.split('.')[1];
    const { exp } = JSON.parse(atob(payload.replace(/-/g, '+').replace(/_/g, '/')));
    return typeof exp === 'number' ? exp * 1000 : null;
  } catch {
    return null;
  }
}

const Decagon = ({ activeVenue, env, isAuthenticated, language, userEmail, venuesList }) => {
  const intl = useIntl();
  const [decagonAuth, setDecagonAuth] = useState(null);
  const [isDecagonUnavailable, setIsDecagonUnavailable] = useState(false);

  const partnersTokenRef = useRef(null);
  const partnersJwtInFlightRef = useRef(false);
  const partnersJwtRefreshTimerRef = useRef(null);
  const partnersJwtDenialCountRef = useRef(0);
  const fetchPartnersJwtRef = useRef(null);
  const isMountedRef = useRef(true);

  const [searchParams, setSearchParams] = useSearchParams();
  // React Router's useSearchParams hook can use stale params, so we need to use a wrapper
  // callback to ensure we set params correctly.
  // See https://github.com/remix-run/react-router/issues/9757
  const paramsRef = useRef({ params: searchParams, setParams: setSearchParams });
  useEffect(() => {
    paramsRef.current = { params: searchParams, setParams: setSearchParams };
  }, [searchParams, setSearchParams]);

  const updateSearchParams = useCallback(paramsInitFn => {
    const { params, setParams } = paramsRef.current;
    const paramsResult = paramsInitFn(params);
    const newParams = new URLSearchParams(paramsResult);

    paramsRef.current.searchParams = newParams;
    setParams(newParams);
  }, []);

  const fetchWithRetry = useCallback(
    async (fetchFn, { maxAttempts = 3, expectedStatuses = [], onExpected, onExhausted } = {}) => {
      const delays = [0, 1000, 2000];

      const attempt = async index => {
        if (index > 0) {
          await new Promise(r => setTimeout(r, delays[index]));
        }
        try {
          return await fetchFn();
        } catch (error) {
          const status = error?.response?.status;
          if (status && expectedStatuses.includes(status)) {
            onExpected?.(status);
            return;
          }
          const isRetryable = !status || status >= 500;
          if (!isRetryable || index === maxAttempts - 1) {
            reportError(error);
            onExhausted?.(error);
            return;
          }
          return attempt(index + 1);
        }
      };

      return attempt(0);
    },
    []
  );

  const fetchDecagonAuthForUser = useCallback(async () => {
    const decagonAuthUrl = isAuthenticated ? URLS.user.decagonAuthToken : URLS.user.decagonAuthTokenPublic;
    const expectedStatuses = isAuthenticated ? [] : ANONYMOUS_AUTH_EXPECTED_DENIAL_STATUSES;
    const requestConfig = isAuthenticated
      ? { ignoreInternalError: true }
      : {
          ignoreInternalError: true,
          logErrorToDatadog: status => !ANONYMOUS_AUTH_EXPECTED_DENIAL_STATUSES.includes(status),
        };

    await fetchWithRetry(
      async () => {
        const response = await httpClient.get(decagonAuthUrl, requestConfig);
        setDecagonAuth(transformKeysToCamelCase(GetDecagonAuthSignatureResponse.fromJSON(response.data)));
      },
      {
        expectedStatuses,
        onExpected: () => setIsDecagonUnavailable(true),
      }
    );
  }, [isAuthenticated, fetchWithRetry]);

  const venuesListMetadata = useMemo(
    () =>
      venuesList?.map(({ venue_id, venue_name, venue_subtitle }) => ({
        venueId: venue_id,
        venueName: venue_name,
        venueSubtitle: venue_subtitle,
      })),
    [venuesList]
  );

  const userMetadata = useMemo(
    () =>
      isAuthenticated
        ? {
            email: userEmail,
            activeVenueId: activeVenue?.venueId,
            activeVenueName: activeVenue?.venueName,
            activeVenueType: activeVenue?.serviceTypes,
            activeVenueSources: JSON.stringify(activeVenue?.sources),
            activeVenueCountryCode: activeVenue?.countryCode,
            activeVenueIsManualStudio: activeVenue?.isManualStudio,
            activeVenueIsVisibleOnSearch: activeVenue?.enabled && !activeVenue?.hideInSearch,
            activeVenueAutoSyncStatus: activeVenue?.autosyncStatus,
            activeVenueMsaId: activeVenue?.msaId,
            activeVenueSubType: activeVenue?.venueDashboardSubtype,
            venuesList: JSON.stringify(venuesListMetadata),
          }
        : {},
    [isAuthenticated, userEmail, activeVenue, venuesListMetadata]
  );

  const decagonMetadata = useMemo(
    () => ({
      ...userMetadata,
      env,
      language,
      sandbox: env === 'local' || env === 'development',
    }),
    [userMetadata, env, language]
  );

  const latestDecagonMetadataRef = useRef(decagonMetadata);
  const hasFiredLoginActionRef = useRef(false);
  const hasFiredLoggedActionRef = useRef(false);
  useEffect(() => {
    latestDecagonMetadataRef.current = decagonMetadata;
  }, [decagonMetadata]);

  const schedulePartnersJwtFetch = useCallback(delayMs => {
    if (!isMountedRef.current) {
      return;
    }
    if (partnersJwtRefreshTimerRef.current) {
      clearTimeout(partnersJwtRefreshTimerRef.current);
    }

    partnersJwtRefreshTimerRef.current = setTimeout(() => {
      // The outgoing token is deliberately left in place: it is still valid for
      // PARTNERS_JWT_REFRESH_LEEWAY_MS, and clearing it here would open a window where
      // initializeDecagonUser pushes metadata with no token at all (IOPS-5713).
      fetchPartnersJwtRef.current?.({ isRefresh: true });
    }, delayMs);
  }, []);

  const schedulePartnersJwtRefresh = useCallback(
    token => {
      const expiresAtMs = getJwtExpiryMs(token);
      const lifetimeMs = expiresAtMs ? expiresAtMs - Date.now() : PARTNERS_JWT_FALLBACK_LIFETIME_MS;
      const delayMs = Math.max(lifetimeMs - PARTNERS_JWT_REFRESH_LEEWAY_MS, PARTNERS_JWT_REFRESH_LEEWAY_MS);

      schedulePartnersJwtFetch(delayMs);
    },
    [schedulePartnersJwtFetch]
  );

  const schedulePartnersJwtDenialRetry = useCallback(() => {
    const attempt = partnersJwtDenialCountRef.current;
    partnersJwtDenialCountRef.current = attempt + 1;

    const delayMs = Math.min(PARTNERS_JWT_DENIAL_RETRY_BASE_MS * 2 ** attempt, PARTNERS_JWT_DENIAL_RETRY_MAX_MS);

    schedulePartnersJwtFetch(delayMs);
  }, [schedulePartnersJwtFetch]);

  const fetchDecagonPartnersAuthApiToken = useCallback(
    async ({ isRefresh = false } = {}) => {
      if (partnersJwtInFlightRef.current) {
        return;
      }
      partnersJwtInFlightRef.current = true;

      try {
        await fetchWithRetry(
          async () => {
            const response = await httpClient.get(URLS.user.decagonPartnersApiAuthToken, { ignoreInternalError: true });
            const parsedResponse = GetPartnersDecagonJwtResponse.fromJSON(response.data);
            const { token } = parsedResponse;

            if (!token) {
              throw new Error('Partners Decagon JWT response contained no token');
            }

            partnersTokenRef.current = token;
            partnersJwtDenialCountRef.current = 0;

            const latestMetadata = latestDecagonMetadataRef.current || decagonMetadata;
            window.duet?.setMetadata(withPartnersToken(latestMetadata, token));

            schedulePartnersJwtRefresh(token);
          },
          {
            expectedStatuses: PARTNERS_JWT_EXPECTED_DENIAL_STATUSES,
            onExpected: status => {
              DatadogRum.addAction(DATADOG_RUM_ACTIONS.DECAGON_JWT_UNAVAILABLE, { status });

              // A denied refresh means the token we are still holding is no longer honoured,
              // so drop it rather than keep pushing one the API will reject.
              if (isRefresh) {
                partnersTokenRef.current = null;
              }

              schedulePartnersJwtDenialRetry();
            },
            // Unlike a denial, a 5xx or network failure says nothing about whether the
            // token we hold is still honoured, so it is kept while we retry.
            onExhausted: () => schedulePartnersJwtDenialRetry(),
          }
        );
      } finally {
        partnersJwtInFlightRef.current = false;
      }
    },
    [decagonMetadata, fetchWithRetry, schedulePartnersJwtRefresh, schedulePartnersJwtDenialRetry]
  );

  useEffect(() => {
    fetchPartnersJwtRef.current = fetchDecagonPartnersAuthApiToken;
  }, [fetchDecagonPartnersAuthApiToken]);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      if (partnersJwtRefreshTimerRef.current) {
        clearTimeout(partnersJwtRefreshTimerRef.current);
      }
    };
  }, []);

  const initializeDecagonUser = () => {
    if (isDecagonUnavailable) {
      return;
    }
    const currentMetadata = latestDecagonMetadataRef.current || decagonMetadata;
    window.duet.setMetadata(withPartnersToken(currentMetadata, partnersTokenRef.current));
    setDecagonMessages(intl, window.duet);

    if (!decagonAuth) {
      fetchDecagonAuthForUser();
    } else {
      const { signature, userId, validUntilEpoch: epoch } = decagonAuth;
      window.duet.setUserId(userId);
      window.duet.setUserIdAuth(signature, epoch);
    }
    if (!partnersTokenRef.current && isAuthenticated) {
      fetchDecagonPartnersAuthApiToken();
    }
  };

  useEffect(() => {
    // The Decagon script is loaded server-side, so we need to wait for it to load before initializing the user
    const decagonScript = document.getElementById('decagon-chat-loader');

    if (decagonScript) {
      const fireDecagonRenderedAction = () => {
        if (isAuthenticated && !hasFiredLoggedActionRef.current) {
          hasFiredLoggedActionRef.current = true;
          DatadogRum.addAction(DATADOG_RUM_ACTIONS.DECAGON_COMPONENT_RENDERED, {
            isAuthenticated: true,
          });
        }

        if (!isAuthenticated && !hasFiredLoginActionRef.current) {
          hasFiredLoginActionRef.current = true;
          DatadogRum.addAction(DATADOG_RUM_ACTIONS.DECAGON_COMPONENT_RENDERED, {
            isAuthenticated: false,
          });
        }
      };

      const handleScriptLoad = () => {
        initializeDecagonUser();
        fireDecagonRenderedAction();
      };

      if (window.duet) {
        initializeDecagonUser();
        fireDecagonRenderedAction();
      } else {
        decagonScript.addEventListener('load', handleScriptLoad);
      }

      return () => {
        // Ensure listener doesn't leak between renders
        decagonScript.removeEventListener('load', handleScriptLoad);
      };
    }

    return undefined;
  }, [decagonAuth, decagonMetadata, intl, isAuthenticated]);

  useEffect(() => {
    if (window.duet && !isDecagonUnavailable) {
      if (searchParams.get('chat_active') === 'true') {
        window.duet.open();
      }
    }
  }, [searchParams, isDecagonUnavailable]);

  useEffect(() => {
    const handleDecagonEvent = event => {
      if (event.eventName === 'DUET_DEINITIALIZE') {
        updateSearchParams(params => {
          if (params.get('chat_active')) {
            params.delete('chat_active');
          }
          return params;
        });
      } else if (event.eventName === 'DUET_USER_SEND') {
        triggerEngagedWithChatBotBraze();
      }
    };

    if (window.duet) {
      window.duet.setEventListener(handleDecagonEvent);

      return () => {
        // Reset listener so we don't stack multiple handlers
        window.duet.setEventListener(null);
      };
    }

    return undefined;
  }, [updateSearchParams]);

  return null;
};

Decagon.propTypes = {
  activeVenue: PropTypes.shape(venuePropTypes),
  env: PropTypes.string,
  isAuthenticated: PropTypes.bool,
  language: PropTypes.string,
  userEmail: PropTypes.string,
  venuesList: PropTypes.arrayOf(
    PropTypes.shape({ venue_id: PropTypes.number, venue_name: PropTypes.string, venue_subtitle: PropTypes.string })
  ),
};

export default Decagon;
